import { Stack, xcss } from '@atlaskit/primitives';
import ProfileCardClient from '@atlaskit/profilecard/client';
import ProfileCard from '@atlaskit/profilecard/user';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseFieldType, USER_VALUE_CURRENT } from '@databases/types/database';
import { UserCard } from '@databases/user';
import { isNonEmptyUserValue } from '@databases/utils/database/field-types/user';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import React, { type FunctionComponent, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { type ViewComponentProps } from './types';

const userContainerStyles = xcss({
  listStyle: 'none',
  padding: 'space.0',
  width: '100%',
  gap: 'space.025',
});

const resourceClient = new ProfileCardClient({
  url: '/gateway/api/directory/graphql',
  productIdentifier: 'confluence',
});

const UserValueView: FunctionComponent<ViewComponentProps<DatabaseFieldType.USER>> = ({
  value,
  showDisabledValue,
}) => {
  const { formatMessage } = useIntl();
  const currentUserId = defaultStoreUtils.useSelector(selectCurrentUserId);

  const resolvedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.map((user) => {
        if (
          'accountId' in user &&
          user.accountId === USER_VALUE_CURRENT &&
          currentUserId
        ) {
          return { _id: user._id, accountId: currentUserId };
        } else {
          return user;
        }
      });
    } else {
      return [];
    }
  }, [currentUserId, value]);

  const createProfileAction = useCallback(
    (accountId: string) => ({
      label: formatMessage(messages['user-value-value.profile-card-view-profile-label']),
      callback: () =>
        globalThis.open(
          `/wiki/people/${accountId}?ref=confluence`,
          '_blank',
          'noopener,noreferrer'
        ),
    }),
    [formatMessage]
  );

  const avatarWrapper = useCallback(
    (accountId: string) => {
      const WrappedAvatar = (avatar: React.ReactNode) => {
        return (
          <ProfileCard
            resourceClient={resourceClient}
            userId={accountId}
            actions={[createProfileAction(accountId)]}
            viewingUserId={currentUserId}
          >
            {avatar}
          </ProfileCard>
        );
      };

      return WrappedAvatar;
    },
    [currentUserId, createProfileAction]
  );

  return (
    <Stack as="ul" xcss={userContainerStyles}>
      {resolvedValue.filter(isNonEmptyUserValue).map((user) => (
        <li key={user._id}>
          <UserCard
            accountId={user.accountId}
            currentUserId={currentUserId}
            displayName={user.displayName}
            variant="avatar"
            showDisabledValue={showDisabledValue}
            avatarWrapper={avatarWrapper(user.accountId)}
          />
        </li>
      ))}
    </Stack>
  );
};

export default UserValueView;
